import { MenuCategory } from './types';

export const getSettingsMenuCategories = (): readonly MenuCategory[] => {
  return settingsMenuCategories;
};

const settingsMenuCategories = [
  {
    title: 'Personal',
    id: 'personal',
    items: [
      {
        id: 'profile',
        title: 'Profile',
        route: '/settings/profile',
        icon: 'user',
        featureFlags: [],
        keywords: ['account', 'user', 'avatar'],
      },
      {
        id: 'preferences',
        title: 'Preferences',
        route: '/settings/preferences',
        icon: 'preferences',
        featureFlags: [],
        keywords: [
          'sounds',
          'ringtones',
          'notifications',
          'keyboard shortcuts',
          'copilot',
          'summaries',
          'summarization',
          'devices',
          'busylight',
          'jabra',
          'appearance',
          'theme',
          'dark mode',
        ],
      },
      {
        id: 'copilotTrial',
        title: 'Co-pilot trial',
        route: '/settings/copilot-trial',
        icon: 'ai',
        featureFlags: [{ name: 'agent-copilot-trial-eligibility' }],
        keywords: ['copilot'],
      },
    ],
  },
  {
    title: 'General',
    id: 'general',
    items: [
      {
        id: 'organization',
        title: 'Organization',
        route: '/settings/organization',
        icon: 'building',
        accessControl: ['organization.info'],
        featureFlags: [],
        keywords: ['company', 'logo', 'timezone'],
      },
      {
        id: 'agents',
        title: 'Agents',
        route: '/settings/agents',
        icon: 'headset',
        accessControl: [
          [
            'organization.member',
            'organization.access',
            'organization.workload',
          ],
          { strategy: 'any' },
        ],
        featureFlags: [],
        keywords: [
          'members',
          'workloads',
          'access',
          'roles',
          'permissions',
          'away status',
        ],
      },
      {
        id: 'teams',
        title: 'Teams',
        route: '/settings/teams',
        icon: 'team',
        accessControl: ['organization.team'],
        featureFlags: [
          { name: 'teams-setting-page-and-assigning-teams-to-queues' },
        ],
        keywords: ['members', 'departments', 'groups'],
      },
      {
        id: 'billing',
        title: 'Billing',
        route: '/settings/billing',
        icon: 'credit-card',
        accessControl: ['organization.billing'],
        featureFlags: [{ name: 'shopify-marketplace-app', invert: true }],
        keywords: ['credit card', 'payment', 'fees', 'subscription'],
      },
    ],
  },
  {
    title: 'Channels and flows',
    id: 'channels_and_flows',
    items: [
      {
        id: 'queues',
        title: 'Queues',
        route: '/settings/queues',
        icon: 'users',
        accessControl: ['settings.queue:r'],
        featureFlags: [],
        keywords: [
          'alerts',
          'offers',
          'routing',
          'sla',
          'service level agreement',
        ],
      },
      {
        id: 'phone',
        title: 'Phone',
        route: '/settings/phone',
        icon: 'phone',
        accessControl: [
          ['settings.integration.channel.edit'],
          { strategy: 'all' },
        ],
        featureFlags: [{ name: 'phone-channel' }],
        keywords: [
          'calls',
          'telephony',
          'voice',
          'numbers',
          'hold music',
          'caller id',
        ],
      },
      {
        id: 'email',
        title: 'Email',
        route: '/settings/email',
        icon: 'mail',
        accessControl: [
          ['settings.integration.channel.edit'],
          { strategy: 'all' },
        ],
        featureFlags: [{ name: 'email-channel' }],
        keywords: ['domains', 'signature', 'sender name', 'email template'],
      },
      {
        id: 'customForms',
        title: 'Forms',
        route: '/settings/custom-forms',
        icon: 'list-ul',
        accessControl: ['settings.custom_attribute'],
        featureFlags: [{ name: 'has-custom-forms-feature' }],
        keywords: ['custom forms', 'contact forms', 'embedded forms'],
      },
      {
        id: 'chat',
        title: 'Chat',
        route: '/settings/chat',
        icon: 'chat',
        accessControl: [
          ['settings.integration.channel.edit'],
          { strategy: 'all' },
        ],
        featureFlags: [{ name: 'widget-chat-channel' }],
        keywords: ['widget', 'live chat'],
      },
      {
        id: 'messenger',
        title: 'Messenger',
        route: '/settings/dixa-messenger',
        icon: 'messenger',
        accessControl: [
          ['settings.integration.channel.edit'],
          { strategy: 'all' },
        ],
        featureFlags: [{ name: 'has-messenger' }],
        keywords: ['dixa messenger', 'widget', 'live chat'],
      },
      {
        id: 'sms',
        title: 'Texts',
        route: '/settings/sms',
        icon: 'sms',
        accessControl: [
          [
            'settings.integration.channel.setup',
            'settings.integration.channel.edit',
          ],
          { strategy: 'all' },
        ],
        featureFlags: [{ name: 'sms-channel' }],
        keywords: ['text message', 'sms', 'mms', 'mobile'],
      },
      {
        id: 'messaging',
        title: 'Third-party messaging',
        route: '/settings/messaging',
        icon: 'messaging-settings',
        accessControl: [
          [
            'settings.integration.channel.setup',
            'settings.integration.channel.edit',
          ],
          { strategy: 'all' },
        ],
        featureFlags: [
          { name: 'facebook-messenger-channel' },
          { name: 'instagram-management' },
          { name: 'whatsapp-channel' },
        ],
        featureFlagsStrategy: 'any',
        keywords: [
          'facebook',
          'instagram',
          'twitter',
          'whatsapp',
          'messaging',
          'social media',
          'messenger',
        ],
      },
      {
        id: 'reviews',
        title: 'Reviews',
        route: '/settings/reviews',
        icon: 'star',
        accessControl: ['settings.integration.channel.edit'],
        featureFlags: [
          { name: 'trustpilot-channel' },
          { name: 'yotpo-channel' },
        ],
        featureFlagsStrategy: 'any',
        keywords: ['trustpilot', 'yotpo', 'ratings'],
      },
    ],
  },
  {
    title: 'Automation and AI',
    id: 'automation_and_ai',
    items: [
      {
        id: 'aiAgents',
        title: 'AI agents',
        route: '/settings/ai-agents',
        icon: 'chat-bot',
        accessControl: [
          ['settings.integration.channel.edit', 'settings.chatbot'],
          { strategy: 'all' },
        ],
        featureFlags: [
          { name: 'mim-chatbot' },
          { name: 'ada-chat-channel' },
          { name: 'digitalgenius-chat-channel' },
          { name: 'kindly-chat-channel' },
          { name: 'generic-api-messaging-channel' },
        ],
        featureFlagsStrategy: 'any',
        keywords: [
          'mim',
          'ada',
          'digitalgenius',
          'kindly',
          'genericapimessaging',
          'chatbot',
        ],
      },
      {
        id: 'automations',
        title: 'Automations',
        route: '/settings/automations',
        icon: 'merge',
        accessControl: ['settings.flow:r'],
        featureFlags: [{ name: 'automations' }],
        keywords: ['flows', 'triggers', 'actions', 'workflows'],
      },
      {
        id: 'intentDetection',
        title: 'Intent detection',
        route: '/settings/intent-detection',
        icon: 'lightbulb',
        accessControl: ['settings.intent_detection'],
        featureFlags: [{ name: 'intent-detection' }],
        featureFlagsStrategy: 'any',
        keywords: ['intents', 'spam detection', 'spam filter'],
      },
      {
        id: 'autoRedaction',
        title: 'Auto-redaction',
        route: '/settings/auto-redaction',
        icon: 'eye-slash',
        accessControl: ['settings.copilot'],
        featureFlags: [{ name: 'message-auto-redaction' }],
        keywords: [
          'redaction',
          'pii',
          'privacy',
          'credit card',
          'iban',
          'email',
          'phone',
        ],
      },
      {
        // Uses useCopilotTrial hook behind the scenes to determine if the item should be shown
        // So it technically uses a feature flag, but it is not listed here
        id: 'writingStyleGuide',
        title: 'Writing style guide',
        route: '/settings/ai/style-guide',
        icon: 'widget-customize',
        accessControl: ['settings.writing_style'],
        featureFlags: [],
        keywords: [
          'style guide',
          'writing style',
          'tone of voice',
          'language',
          'grammar',
          'spelling',
        ],
      },
    ],
  },
  {
    title: 'Customization',
    id: 'manage',
    items: [
      {
        id: 'audioFiles',
        title: 'Audio files',
        route: '/settings/audio',
        icon: 'volume-up',
        accessControl: ['settings.audio_file'],
        featureFlags: [],
        keywords: ['announcement', 'wait music'],
      },
      {
        id: 'businesshours',
        title: 'Business hours',
        route: '/settings/businesshours',
        icon: 'clock-three',
        accessControl: ['settings.business_hours'],
        featureFlags: [],
        keywords: [
          'opening hours',
          'holidays',
          'exceptions',
          'availability',
          'schedule',
        ],
      },
      {
        id: 'customAttributes',
        title: 'Custom attributes',
        route: '/settings/custom-attributes',
        icon: 'input',
        accessControl: ['settings.custom_attribute'],
        featureFlags: [],
        keywords: [
          'conversation attributes',
          'contact attributes',
          'custom fields',
        ],
      },
      {
        id: 'helpCenters',
        title: 'Help centers',
        route: '/settings/help-centers',
        icon: 'question',
        accessControl: ['settings.helpcenter:w'],
        featureFlags: [
          {
            name: 'help-center',
          },
        ],
        keywords: ['help center', 'knowledge base', 'kb'],
      },
      {
        id: 'integrations',
        title: 'Integrations',
        route: '/settings/integrations',
        icon: 'plug',
        accessControl: ['settings.integration.api'],
        featureFlags: [],
        keywords: [
          'custom cards',
          'github',
          'jira',
          'klaviyo',
          'linear',
          'loyaltylion',
          'magento',
          'miuros',
          'salesforce',
          'shopify',
          'voyado',
          'third-party',
          'external services',
          'api tokens',
          'webhooks',
          'webform endpoints',
          'ai agents',
          'solvemate',
        ],
      },
      {
        id: 'satisfaction',
        title: 'Satisfaction',
        route: '/settings/satisfaction',
        icon: 'smile',
        accessControl: ['settings.satisfaction'],
        featureFlags: [
          { name: 'csat-for-email' },
          { name: 'csat-for-widget-chat' },
          { name: 'csat-for-dixa-messenger' },
          { name: 'agnostic-csat-settings' },
        ],
        featureFlagsStrategy: 'any',
        keywords: [
          'customer satisfaction',
          'csat',
          'nps',
          'net promoter score',
          'customer feedback',
          'surveys',
          'ratings',
        ],
      },
      {
        id: 'sla',
        title: 'SLA',
        route: '/settings/sla',
        icon: 'hourglass-clock',
        accessControl: ['settings.sla'],
        featureFlags: [],
        keywords: [
          'service level agreement',
          'sla policies',
          'response time',
          'resolution time',
        ],
      },
      {
        id: 'tags',
        title: 'Tags',
        route: '/settings/tags',
        icon: 'tag',
        accessControl: ['settings.tag'],
        featureFlags: [],
        keywords: ['labels', 'categories', 'tagging'],
      },
      {
        id: 'templates',
        title: 'Templates',
        route: '/settings/templates',
        icon: 'bolt',
        accessControl: ['settings.template'],
        featureFlags: [],
        keywords: [
          'quick replies',
          'quick responses',
          'auto replies',
          'email templates',
          'macros',
        ],
      },
    ],
  },
  {
    title: 'Other',
    id: 'other',
    items: [
      {
        id: 'bannedUsers',
        title: 'Banned users',
        route: '/settings/banned-users',
        icon: 'ban',
        accessControl: ['settings.contact_ban'],
        featureFlags: [],
        keywords: ['blocked users', 'blacklist', 'ip addresses'],
      },
      {
        id: 'knowledge',
        title: 'Knowledge management',
        route: '/settings/knowledge',
        icon: 'elevio',
        featureFlags: [{ name: 'knowledge-management-nav' }],
        keywords: ['elevio'],
      },
      {
        id: 'exportConversations',
        title: 'Export conversations',
        route: '/settings/export-conversations',
        icon: 'download',
        accessControl: ['conversation.export'],
        featureFlags: [{ name: 'export-conversations' }],
        keywords: ['export', 'download', 'conversation history'],
      },
      {
        id: 'scheduledConversations',
        title: 'Scheduled conversations',
        route: '/settings/scheduled-conversations',
        icon: 'calendar',
        accessControl: ['conversation.import'],
        featureFlags: [{ name: 'scheduled-conversations' }],
        keywords: ['scheduled', 'callback conversations'],
      },
    ],
  },
  {
    title: 'About',
    id: 'about',
    items: [
      {
        id: 'versions',
        title: 'Versions',
        route: '/settings/about',
        icon: 'dixa',
        featureFlags: [],
        keywords: [],
      },
    ],
  },
] as const satisfies readonly MenuCategory[];

export const menuCategoryIds = settingsMenuCategories.map(
  (category) => category.id,
);
export type MenuCategoryId = (typeof settingsMenuCategories)[number]['id'];
